import { useEffect } from 'react'
import { createPortal } from 'react-dom'

import type { ReactNode } from 'react'

import CloseIcon from '@/assets/icons/close.svg?react'

import { Backdrop, CloseButton, Sheet, SheetBody, SheetFooter, SheetHeader, SheetTitle } from './styles'

interface BottomSheetProps {
  open: boolean
  onClose: () => void
  title?: string
  footer?: ReactNode
  children: ReactNode
}

/**
 * 공통 BottomSheet — 화면 하단 슬라이드 시트.
 *
 * Backdrop 클릭 / ESC 키로 닫힘. body 에 portal 로 렌더해서 부모 stacking
 * context 영향 받지 않음.
 */
export function BottomSheet({ open, onClose, title, footer, children }: BottomSheetProps) {
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <Backdrop onClick={onClose}>
      <Sheet
        role='dialog'
        aria-modal='true'
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <CloseButton
              type='button'
              aria-label='닫기'
              onClick={onClose}
            >
              <CloseIcon
                width={14}
                height={14}
              />
            </CloseButton>
          </SheetHeader>
        )}
        <SheetBody>{children}</SheetBody>
        {footer && <SheetFooter>{footer}</SheetFooter>}
      </Sheet>
    </Backdrop>,
    document.body,
  )
}
